import { useCallback, useMemo } from 'react'

import { MultiSelect } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { FieldTemplateForViewFragment } from 'types/graphql'

import {
  useAllFieldTemplatesInOrgQuery,
  useAllFieldTemplatesInRecordTypeQuery,
} from 'src/fetch/recordTypes'
import { dedupeObjectsById, filterNil } from 'src/utils'

type Props = {
  errorMessage?: string
  isInvalid?: boolean
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onChange: (values: FieldTemplateForViewFragment[] | undefined) => void
  value: FieldTemplateForViewFragment[] | undefined
  recordTypeId: number | null | undefined
}

const SharedFieldGroupLabel = 'Shared Field Groups'

type RecordTemplateWithContext = {
  recordTypeName: string | null
  recordTemplate: {
    id: number
    name?: string | null
    applicationFields?: (FieldTemplateForViewFragment | null)[] | null
  }
}

const FieldTemplateMultiSelect = ({
  errorMessage,
  isInvalid,
  onBlur,
  onChange,
  value,
  recordTypeId,
}: Props) => {
  const isOrgWide = recordTypeId == null
  const recordTypeQuery = useAllFieldTemplatesInRecordTypeQuery(recordTypeId)
  const orgQuery = useAllFieldTemplatesInOrgQuery({ enabled: isOrgWide })
  const isLoading = isOrgWide ? orgQuery.isLoading : recordTypeQuery.isLoading
  const fieldType = value?.[0]?.type

  const recordTemplatesWithContext: RecordTemplateWithContext[] = useMemo(() => {
    if (isOrgWide) {
      return (
        orgQuery.data?.recordTypesUserHasAccessTo?.flatMap((recordType) =>
          filterNil(recordType.recordTemplates ?? []).map((recordTemplate) => ({
            recordTypeName: recordType.name,
            recordTemplate,
          }))
        ) ?? []
      )
    }
    return filterNil(recordTypeQuery.data?.recordType?.recordTemplates ?? []).map(
      (recordTemplate) => ({
        recordTypeName: null,
        recordTemplate,
      })
    )
  }, [isOrgWide, orgQuery.data, recordTypeQuery.data])

  const options: SelectOption<FieldTemplateForViewFragment>[] = useMemo(() => {
    const fields = filterNil(
      recordTemplatesWithContext.flatMap(
        ({ recordTypeName, recordTemplate: rt }) =>
          rt.applicationFields
            ?.filter((f) => f && (!fieldType || f.type === fieldType))
            ?.map((f) => {
              if (!f) {
                return null
              }
              const baseGroup = f.fieldGroupTemplate?.isSharedFieldGroup
                ? SharedFieldGroupLabel
                : `${rt.name || 'No Name'}`
              const group =
                isOrgWide && recordTypeName && !f.fieldGroupTemplate?.isSharedFieldGroup
                  ? `${recordTypeName} > ${baseGroup}`
                  : baseGroup
              return {
                id: f.id,
                group,
                label: `${group}: ${f.label}`,
                customDisplay: (v: FieldTemplateForViewFragment) => v.label,
                value: f,
              }
            }) ?? []
      )
    )
    const dedupedFields: SelectOption<FieldTemplateForViewFragment>[] = dedupeObjectsById(
      fields
    ).map((f) => ({
      group: f.group,
      label: f.label,
      value: f.value,
      customDisplay: (v: FieldTemplateForViewFragment) => v.label,
    }))

    for (const field of value ?? []) {
      if (field.isArchived) {
        const groupName = field.fieldGroupTemplate?.isSharedFieldGroup
          ? SharedFieldGroupLabel
          : field.fieldGroupTemplate?.usedInRecordTemplates?.[0]?.name || 'No Name'
        dedupedFields.unshift({
          group: groupName,
          label: `[ARCHIVED] ${groupName}: ${field.label}`,
          customDisplay: (v: FieldTemplateForViewFragment) => `[ARCHIVED] ${v.label}`,
          value: field,
        })
      }
    }

    const sortedFields = dedupedFields.sort((a, b) => {
      if (a.group !== b.group) {
        if (a.group === SharedFieldGroupLabel) {
          return -1
        }
        if (b.group === SharedFieldGroupLabel) {
          return 1
        }

        return (a.group || '').localeCompare(b.group || '')
      }

      return (a.label || '').localeCompare(b.label || '')
    })

    return sortedFields
  }, [recordTemplatesWithContext, fieldType, value, isOrgWide])
  const optionsById = useMemo(
    () =>
      new Map<number, SelectOption<FieldTemplateForViewFragment>>(
        options.map((o) => [o.value.id, o])
      ),
    [options]
  )
  const selectedOptions = useMemo(
    () => filterNil(value?.map((v) => optionsById.get(v.id)) ?? []),
    [optionsById, value]
  )

  const getOptionKey = useCallback(
    (o: SelectOption<FieldTemplateForViewFragment>) => o.value.id,
    []
  )

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<FieldTemplateForViewFragment>[] | undefined) => {
      onChange(newSelectedOptions?.map((o) => o.value))
    },
    [onChange]
  )

  return (
    <MultiSelect<FieldTemplateForViewFragment>
      caption="All fields selected must share the same field type."
      errorMessage={errorMessage}
      getOptionKey={getOptionKey}
      isDisabled={isLoading}
      isInvalid={isInvalid}
      isLoading={isLoading}
      isRequired
      label="Fields to Display"
      onBlur={onBlur}
      onSelectedOptionsChange={handleSelectedOptionsChange}
      options={options}
      placeholder={isLoading ? 'Loading fields...' : undefined}
      selectedOptions={selectedOptions}
    />
  )
}

export default React.memo(FieldTemplateMultiSelect)
